import { type CSSProperties, type JSX, memo, type RefObject } from "react";
import { Bar, BarChart, ReferenceLine, XAxis, YAxis } from "recharts";
import { currentColors } from "../../colors";
import maxTextWidth from "../../maxTextWidth";
import t from "../../translations";
import { Legend, LegendLabel, LegendLine } from "./stylings";

export interface IChartDatum {
    label: string;
    value: number;
}

export interface IProps {
    chartData: IChartDatum[];
    checkpoint: number;
    goal: number;
    deadlineGoal: number;
    chartRef?: RefObject<HTMLDivElement>;
    style?: CSSProperties;
}

function milesFormat(miles: number): string {
    if (!miles) {
        return "";
    }
    return miles.toFixed(0);
}

function tickFormatter(miles: number): string {
    return miles.toFixed(0);
}

const FONT = '400 15px / 1.5 "Proxima Nova", Verdana, sans-serif';

export function createDomainWithReferenceLines(
    ...referenceValues: number[]
): [number, (dataMax: number) => number] | [number, string] {
    const validReferences = referenceValues.filter((v) => v > 0);
    return validReferences.length > 0 ? [0, (dataMax: number) => Math.max(dataMax, ...validReferences)] : [0, "auto"];
}

function MilesBarChart({ chartData, checkpoint, goal, deadlineGoal, chartRef, style }: IProps): JSX.Element {
    const height = chartData.length * 32 + 40;
    const showLegend = !!checkpoint || !!goal || !!deadlineGoal;
    const maxNameSize = maxTextWidth(
        chartData.map((datum) => datum.label),
        FONT,
    );

    const maxDataValue = Math.max(...chartData.map((d) => d.value), 0);
    const domainFn = createDomainWithReferenceLines(checkpoint, goal, deadlineGoal);
    const domain = typeof domainFn[1] === "function" ? ([0, domainFn[1](maxDataValue)] as [number, number]) : domainFn;

    return (
        <div style={style} ref={chartRef}>
            <div>
                <BarChart data={chartData} layout="vertical" width="100%" height={height} responsive>
                    <YAxis dataKey="label" type="category" width={maxNameSize + 20} tick={{ fontSize: 15 }} />
                    <XAxis type="number" tickFormatter={tickFormatter} domain={domain} />
                    <Bar
                        isAnimationActive={false}
                        dataKey="value"
                        fill={currentColors.theme1}
                        label={{ fill: "white", position: "insideRight", formatter: milesFormat, fontSize: 14 }}
                    />
                    {!!checkpoint && <ReferenceLine x={checkpoint} stroke={currentColors.grey4} strokeWidth={4} />}
                    {!!goal && <ReferenceLine x={goal} stroke={currentColors.taskDeadline} strokeWidth={4} />}
                    {!!deadlineGoal && <ReferenceLine x={deadlineGoal} stroke={currentColors.error} strokeWidth={4} />}
                </BarChart>
            </div>
            {showLegend && (
                <Legend>
                    {!!checkpoint && (
                        <LegendLabel>
                            <LegendLine $color={currentColors.grey4} />
                            {t("shared.learning-path-activity-charts.label-checkbpoint")}
                        </LegendLabel>
                    )}
                    {!!goal && (
                        <LegendLabel>
                            <LegendLine $color={currentColors.taskDeadline} />
                            {t("shared.learning-path-activity-charts.label-goal")}
                        </LegendLabel>
                    )}
                    {!!deadlineGoal && (
                        <LegendLabel>
                            <LegendLine $color={currentColors.error} />
                            {t("shared.learning-path-activity-charts.label-deadline")}
                        </LegendLabel>
                    )}
                </Legend>
            )}
        </div>
    );
}

export default memo(MilesBarChart);
